/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useForm,
  SubmitHandler,
  FormProvider,
  SubmitErrorHandler,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Input from "components/Input";
// import inventoryManager from "services/Inventory/inventoryManager";
// import { InventoryPayload } from "services/Inventory/inventoryInterface";
import Select from "react-select";
import apiService from "services/apiService";
// Admin-configured options (Asset/Device/Status) + file upload policy (types/size).
import { useInventoryOptions, useInventoryUploadConfig, normalizeAssetType } from "./useInventoryConfig";
// Reusable uploader: dropzone + file info + shared MediaPreview lightbox.
import { InventoryImageUploader } from "./InventoryImageUploader";
import { sortByKey } from "util/sortOptions";
import { useToaster } from "contexts/toasterContext";
import { colors } from "themes";
import { customSelectStyles } from "util/selectStyles";
import { handleNameInput } from "util/Util";
import { validateNoLeadingTrailingWhitespace } from "util/validation";
interface OptionProps {
  value: string;
  label: string;
}

const toDateOnly = (value: unknown): Date | null => {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === "string") {
    const parsed = new Date(`${value}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
  }

  const parsed = new Date(value as any);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const schema = yup.object().shape({
  assign: yup.string().required("Assign is required"),
  type: yup.string().required("Type is required"),
  status: yup.string().required("Status is required"),
  owner: yup.string().required("Owner is required"),
  location: yup.string().required("Location is required").test(
    "not-only-spaces",
    "Location cannot be empty spaces", // Updated error message
    validateNoLeadingTrailingWhitespace),
  model: yup.string().required("Model is required"),
  identity: yup.string().required("Inventory Identity Number is required").test(
    "not-only-spaces",
    "Inventory Identity Number cannot be empty spaces",
    (value) => {
      return value ? value.trim().length > 0 : false;
    }
  ),
  brand: yup.string().required("Brand is required").test(
    "not-only-spaces",
    "Brand cannot be empty spaces",
    (value) => {
      return value ? value.trim().length > 0 : false;
    }),
  assignDate: yup
    .string()
    .required("Assign Date is required")
    .test(
      "assign-gte-purchase",
      "Assign Date should be same or after Purchase Date",
      function (assignDate) {
        const purchaseDate = this.parent.purchaseDate;
        if (!assignDate || !purchaseDate) return true;

        const assign = toDateOnly(assignDate);
        const purchase = toDateOnly(purchaseDate);
        if (!assign || !purchase) return true;

        return assign >= purchase;
      }
    ),
  purchaseDate: yup
    .string()
    .required("Purchase Date is required")
    .test(
      "purchase-lte-assign",
      "Purchase Date should be before or same as Assign Date",
      function (purchaseDate) {
        const assignDate = this.parent.assignDate;
        if (!purchaseDate || !assignDate) return true;

        const purchase = toDateOnly(purchaseDate);
        const assign = toDateOnly(assignDate);
        if (!purchase || !assign) return true;

        return purchase <= assign;
      }
    )
    .test(
      "purchase-lte-release",
      "Purchase Date should be before or same as Release Date",
      function (purchaseDate) {
        const releaseDate = this.parent.releaseDate;
        if (!purchaseDate || !releaseDate) return true;

        const purchase = toDateOnly(purchaseDate);
        const release = toDateOnly(releaseDate);
        if (!purchase || !release) return true;

        return purchase <= release;
      }
    ),
  releaseDate: yup
    .string()
    // .required("Release Date is required")
    .test(
      "release-gte-purchase",
      "Release Date should be same or after Purchase Date",
      function (releaseDate) {
        const purchaseDate = this.parent.purchaseDate;
        if (!releaseDate || !purchaseDate) return true;

        const release = toDateOnly(releaseDate);
        const purchase = toDateOnly(purchaseDate);
        if (!release || !purchase) return true;

        return release >= purchase;
      }
    ),
  asset_type: yup.string().required("Inventory type is required"),
  userEmail: yup.string().required("User Email is required"),
  cost: yup.number().when("asset_type", {
    is: (val: string) => normalizeAssetType(val) === "own",
    then: (schema) =>
      schema
        .transform((value, originalValue) =>
          originalValue === "" || originalValue === null || originalValue === undefined
            ? undefined
            : value
        )
        .required("Cost is required") // <-- Added missing required rule
        .typeError("Please enter a valid number for cost") // <-- Added type error fallback
        .min(0, "Cost must be a non-negative number"),
    otherwise: (schema) =>
      schema.transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined
          ? undefined
          : value
      ),
  }),
  depreciationRate: yup.number().when("asset_type", {
    is: (val: string) => normalizeAssetType(val) === "own",
    then: (schema) =>
      schema
        .transform((value, originalValue) =>
          originalValue === "" || originalValue === null || originalValue === undefined
            ? undefined
            : value
        )
        .required("Depreciation Rate is required") // <-- Added missing required rule
        .typeError("Please enter a valid number for depreciation rate") // <-- Added type error fallback
        .min(0, "Depreciation Rate must be a non-negative number")
        .max(100, "Depreciation rate cannot be greater than 100"),
    otherwise: (schema) =>
      schema.transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined
          ? undefined
          : value
      ),
  }),
  rentAmount: yup.number().when("asset_type", {
    is: (val: string) => normalizeAssetType(val) === "rental",
    then: (schema) =>
      schema
        .transform((value, originalValue) =>
          originalValue === "" || originalValue === null || originalValue === undefined
            ? undefined
            : value
        )
        .required("Rental Amount is required")
        .typeError("Please enter a valid number for rent amount")
        .min(0, "Rent Amount must be a non-negative number"),
    otherwise: (schema) =>
      schema.transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined
          ? undefined
          : value
      ),
  }),
  rentStartDate: yup.string().when("asset_type", {
    is: (val: string) => normalizeAssetType(val) === "rental",
    then: (schema) => schema.required("Assign Date is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  issueDate: yup.string().when("asset_type", {
    is: (val: string) => normalizeAssetType(val) === "sample",
    then: (schema) => schema.required("Issued Date is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  returnDate: yup.string().when("asset_type", {
    is: (val: string) => normalizeAssetType(val) === "sample",
    then: (schema) => schema.required("Return Date is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

interface AddProductProps {
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setRefresh: any;
  refresh: number;
}

const AddProduct = ({ onClose, setRefresh, refresh }: AddProductProps) => {
  const methods = useForm({ resolver: yupResolver(schema) });
  // Drives the asset-type conditional fields (Own/Rental/Sample) below. Watched
  // directly from the form so Status/other selects can never clobber it.
  const selectedAssetType = methods.watch("asset_type");
  const [employeeOptions, setEmployeeOptions] = useState<OptionProps[]>([]);
  const [device, setDevice] = useState();
  const [step, setStep] = useState(0);
  const [images, setImages] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedUser, setSelectedUser] = useState<any>();
  const [assetType, setAssetType] = useState<any>();
  const [status, setStatus] = useState<any>();
  const [userPage, setUserPage] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const USER_PAGE_SIZE = 10;
  console.log("isSubmitting :>> ", isSubmitting);

  // Admin-configured dropdown options + file upload policy (types/size/count).
  const { assetTypeOptions, deviceTypeOptions, deviceStatusOptions } = useInventoryOptions();
  const uploadConfig = useInventoryUploadConfig();
  const employeeSelectStyles = useMemo(
    () => ({
      ...customSelectStyles,
      indicatorsContainer: (base: any) => ({
        ...base,
        minWidth: 64,
        width: 64,
        justifyContent: "flex-end",
      }),
      loadingIndicator: (base: any) => ({
        ...base,
        marginRight: 8,
        padding: 0,
      }),
      indicatorSeparator: (base: any) => ({
        ...base,
        marginLeft: 2,
        marginRight: 2,
      }),
    }),
    []
  );
  const stepOneRequiredFields = [
    "model",
    "brand",
    "type",
    "location",
    "owner",
    "assign",
    "status",
    "identity"
  ] as const;
  type StepOneField = (typeof stepOneRequiredFields)[number];

  const nextStep = async () => {
    const isStepOneValid = await methods.trigger(stepOneRequiredFields, {
      shouldFocus: true,
    });

    if (!isStepOneValid) {
      return;
    }

    setStep((prev) => prev + 1);
  };
  const prevStep = () => setStep((prev) => prev - 1);
  const { addToast } = useToaster();

  useEffect(() => {
    const subscription = methods.watch((values, { name }) => {
      if (
        step === 0 &&
        name &&
        (stepOneRequiredFields as readonly string[]).includes(name)
      ) {
        const fieldName = name as StepOneField;
        const fieldValue = values[fieldName];
        const hasValue =
          typeof fieldValue === "string"
            ? fieldValue.trim() !== ""
            : fieldValue !== undefined && fieldValue !== null && fieldValue !== "";

        if (hasValue) {
          methods.clearErrors(fieldName);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [methods, step]);

  const handleImageUpload = (files: FileList | null) => {
    if (!files) return;
    const fileArray = Array.from(files);

    // Enforce the admin-configured file count limit.
    if (fileArray.length + images.length > uploadConfig.maxFiles) {
      setErrorMessage(`You can upload a maximum of ${uploadConfig.maxFiles} files.`);
      return;
    }

    // Validate each file against the admin-configured allowed types and max size.
    for (const file of fileArray) {
      const error = uploadConfig.validateFile(file);
      if (error) {
        setErrorMessage(error);
        return;
      }
    }

    setImages((prevImages) => [...prevImages, ...fileArray]);
    setErrorMessage("");
  };
  // const removeImage = (index: number) => {
  //   setImages((prevImages) => prevImages.filter((_, i) => i !== index));

  //   // Clear the file input value to allow selecting the same file again
  //   if (fileInputRef.current) {
  //     fileInputRef.current.value = "";
  //   }
  // };

  const removeImage = (indexToRemove: number) => {
    setImages((prevImages) => {
      const updatedImages = prevImages.filter((_, index) => index !== indexToRemove);

      const fileInput = document.querySelector(
        'input[type="file"]'
      ) as HTMLInputElement | null;

      if (fileInput) {
        if (updatedImages.length === 0) {
          // Only show "No file chosen" when all images are removed
          fileInput.value = "";
        } else {
          // Set the native file input to display the last remaining image
          const lastFile = updatedImages[updatedImages.length - 1];
          if (lastFile instanceof File) {
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(lastFile);
            fileInput.files = dataTransfer.files;
          }
        }
      }

      return updatedImages;
    });
  };

  const isEmpty = (obj: any) => {
    return Object.keys(obj).length === 0;
  };

  const getUsersFromPayload = (payload: any) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.content)) {
      return payload.content;
    }

    return [];
  };

  const fetchUsers = async (page: number, searchValue: string, append: boolean) => {
    if (loadingUsers) return;

    setLoadingUsers(true);
    try {
      const query = new URLSearchParams();
      query.set("page", String(page));
      query.set("size", String(USER_PAGE_SIZE));
      if (searchValue.trim()) {
        query.set("search", searchValue.trim());
      }

      const res = await apiService.get(`api/users?${query.toString()}`);
      const payload = res.data;
      const users = getUsersFromPayload(payload);

      // ADDED FILTER HERE: Check if displayName exists and isn't just empty spaces
      const options = users
        .filter((user: any) => user.displayName && user.displayName.trim() !== "")
        .map((user: any) => ({
          value: user.email,
          label: user.displayName,
        }));

      setEmployeeOptions((prev) => {
        const merged = append ? [...prev, ...options] : options;
        const dedup = new Map<string, OptionProps>();
        merged.forEach((item) => {
          if (!dedup.has(item.value)) {
            dedup.set(item.value, item);
          }
        });
        return sortByKey(Array.from(dedup.values()), "label");
      });

      setUserPage(page);
      if (typeof payload?.last === "boolean") {
        setHasMoreUsers(!payload.last);
      } else if (typeof payload?.totalPages === "number") {
        setHasMoreUsers(page < payload.totalPages - 1);
      } else {
        setHasMoreUsers(options.length === USER_PAGE_SIZE);
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setLoadingUsers(false);
    }
  };


  const steps = [
    <div key="step1" className="grid gap-4 sm:grid-cols-3">
      <Input name="model" label="Model" placeholder="Enter Model" required />
      <Input name="brand" label="Brand" placeholder="Enter Brand" required />
      <div className="mb-4">
        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          Select Device Type <span className="text-red-500">*</span>
        </label>
        <Select
          {...methods.register("type", { required: true })}
          options={sortByKey(deviceTypeOptions, "label")}
          placeholder="Device Type"
          className="w-full text-slate-900 dark:text-slate-100"
          styles={customSelectStyles}
          menuPosition="fixed"
          menuPortalTarget={document.body}
          onChange={(selectedOption: OptionProps | null) => {
            if (selectedOption) {
              methods.setValue("type", selectedOption.value, {
                shouldTouch: true,
              });
              setDevice(selectedOption as any);
            }
          }}
          value={device}
        />
        {methods.formState.errors.type && (
          <span className="text-red-500 text-xs font-medium leading-tight mt-1">
            {methods.formState.errors.type.message as string}
          </span>
        )}
      </div>
      <Input
        name="location"
        label="Location"
        placeholder="Enter Location"
        required
      />
      {/* <Input name="status" label="Status" placeholder="Enter Status" required/> */}
      <Input
        name="owner"
        label="Owner"
        placeholder="Enter Owner Name"
        required
        onInput={(e: React.FormEvent<HTMLInputElement>) =>
          handleNameInput(e, methods.setValue)
        }
      />
      <div className="mb-4">
        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          Assign To<span className="text-red-500">*</span>
        </label>
        <Select
          {...methods.register("assign", { required: true })}
          options={employeeOptions}
          placeholder="Select Employee"
          className="w-full text-slate-900 dark:text-slate-100"
          styles={employeeSelectStyles}
          isLoading={loadingUsers}
          isClearable
          menuPosition="fixed"
          menuPortalTarget={document.body}
          onMenuScrollToBottom={() => {
            if (hasMoreUsers && !loadingUsers) {
              void fetchUsers(userPage + 1, userSearch, true);
            }
          }}
          onInputChange={(value, actionMeta) => {
            if (actionMeta.action === "input-change") {
              setUserSearch(value);
            }
            return value;
          }}
          onChange={(selectedOption: OptionProps | null) => {
            if (selectedOption) {
              methods.setValue("assign", selectedOption.label, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
              methods.setValue("userEmail", selectedOption.value, {
                shouldDirty: true,
                shouldTouch: true,
              });
              setSelectedUser(selectedOption as any);
            } else {
              methods.setValue("assign", "", {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
              methods.setValue("userEmail", "", {
                shouldDirty: true,
                shouldTouch: true,
              });
              setSelectedUser(null);
            }
          }}
          value={selectedUser}
        />
        {methods.formState.errors.assign && (
          <span className="text-red-500 text-xs font-medium leading-tight mt-1">
            {methods.formState.errors.assign.message}
          </span>
        )}
      </div>
      <div className="mb-4">
        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          Status<span className="text-red-500">*</span>
        </label>
        <Select
          {...methods.register("status", { required: true })}
          options={deviceStatusOptions}
          placeholder="Select Status"
          className="w-full text-slate-900 dark:text-slate-100"
          styles={customSelectStyles}
          menuPosition="fixed"
          menuPortalTarget={document.body}
          onChange={(selectedOption: OptionProps | null) => {
            if (selectedOption) {
              methods.setValue("status", selectedOption.value, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
              setStatus(selectedOption as any);
            }
          }}
          value={status}
        />
        {methods.formState.errors.status && (
          <span className="text-red-500 text-xs font-medium leading-tight mt-1">
            {methods.formState.errors.status.message}
          </span>
        )}
      </div>
      <Input
        name="assetPassword"
        label="Asset Password"
        placeholder="Enter Asset password"

      />
      <Input
        name="identity"
        type="text"
        label="Inventory Identity Number"
        placeholder="Enter Identity Number"
        required
      />
    </div>,
    <div key="step2" className="grid gap-4 sm:grid-cols-3">
      <Input
        name="userEmail"
        label="User Email"
        type="email"
        placeholder="Enter User Email"
        disabled
      />
      <Input
        name="assignDate"
        type="date"
        label="Assign Date"
        placeholder="Enter Assign Date"
        required
      />
      <Input
        name="purchaseDate"
        type="date"
        label="Purchase Date"
        placeholder="Enter Purchase Date"
        required
      />
      <Input
        name="releaseDate"
        type="date"
        label="Release Date"
        placeholder="Enter Release date"
        // required
      />
      <div className="mb-4">
        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          Asset Type<span className="text-red-500">*</span>
        </label>
        <Select
          {...methods.register("asset_type", { required: true })}
          options={assetTypeOptions}
          placeholder="Asset Type"
          className="w-full text-slate-900 dark:text-slate-100"
          styles={customSelectStyles}
          menuPosition="fixed"
          menuPortalTarget={document.body}
          onChange={(selectedOption: OptionProps | null) => {
            if (selectedOption) {
              methods.setValue("asset_type", selectedOption.value, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
              methods.clearErrors("asset_type");
              setAssetType(selectedOption as any);
            }
          }}
          value={assetType}
        />
        {methods.formState.errors.asset_type && (
          <span className="text-red-500 text-xs font-medium leading-tight mt-1">
            {methods.formState.errors.asset_type.message}
          </span>
        )}
      </div>
      {normalizeAssetType(selectedAssetType) === "sample" && (
        <>
          <Input name="issueDate" type="date" label="Issue Date" required />
          <Input name="returnDate" type="date" label="Return Date" required />
        </>
      )}
      {normalizeAssetType(selectedAssetType) === "own" && (
        <>
          <Input
            name="cost"
            type="number"
            label="Cost"
            placeholder="Eg: 5000"
            required
          />
          <Input
            name="depreciationRate"
            type="number"
            label="Depreciation Rate"
            placeholder="Eg: 10.0"
            required
          />
        </>
      )}
      {normalizeAssetType(selectedAssetType) === "rental" && (
        <>
          <Input
            name="rentAmount"
            type="number"
            label="Rental Amount"
            placeholder="Eg: 5000"
            required
          />
          <Input
            name="rentStartDate"
            type="date"
            label="Rent Start Date"
            required
          />
        </>
      )}
      <div className="mb-4 sm:col-span-3">
        <InventoryImageUploader
          items={images}
          onAddFiles={handleImageUpload}
          onRemove={removeImage}
          accept={uploadConfig.accept}
          helpText={uploadConfig.helpText}
          maxFiles={uploadConfig.maxFiles}
          error={errorMessage}
        />
      </div>
      <div className="flex justify-center">
        {!isEmpty(methods.formState.errors) && (
          <span className="text-red-500 text-sm mt-1">
            Please check mandatory fields{" "}
          </span>
        )}
      </div>
    </div >,
  ];

  //sonar issue fix 
  // reduce cognitive complexity
  const buildUpdatedData = (data: any) => {
    const updatedData = { ...data };

    switch (normalizeAssetType(data.asset_type)) {
      case "own":
        updatedData.ownedDevice = {
          cost: data.cost,
          depreciationRate: data.depreciationRate,
        };
        break;

      case "rental":
        updatedData.rentedDevice = {
          rentStartDate: data.rentStartDate,
          rentAmount: data.rentAmount,
        };
        break;

      case "sample":
        updatedData.sampleDevice = {
          issueDate: data.issueDate,
          returnDate: data.returnDate,
        };
        break;

      default:
        break;
    }

    return updatedData;
  };

  const createAssetFormData = (updatedData: any) => {
    const formData = new FormData();

    const dtoObject = new Blob([JSON.stringify(updatedData)], {
      type: "application/json",
    });

    formData.append("asset", dtoObject);

    return formData;
  };

  const getStorageType = async () => {
    try {
      const storageRes = await apiService.get("api/admin/storage");
      return storageRes?.data?.storage || "DRIVE";
    } catch (error) {
      console.log("Failed to detect storage type, using DRIVE flow", error);
      return "DRIVE";
    }
  };

  const uploadImagesToS3 = async (formData: FormData) => {
    const uploadedKeys: string[] = [];

    for (const image of images) {
      const uploadUrlRes = await apiService.post("api/upload-url", {
        fileName: image.name,
      });

      const { uploadUrl, key } = uploadUrlRes?.data || {};

      if (!uploadUrl || !key) {
        throw new Error("Missing uploadUrl or key from upload-url response");
      }

      const putResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: image,
      });

      if (!putResponse.ok) {
        throw new Error("File upload to storage failed");
      }

      uploadedKeys.push(key);
    }

    uploadedKeys.forEach((key) => {
      formData.append("keys", key);
    });
  };

  const appendDriveImages = (formData: FormData) => {
    images.forEach((image) => {
      formData.append("files", image);
    });
  };

  const handleImageUploadByStorageType = async (
    formData: FormData,
    storageType: string,
  ) => {
    if (images.length === 0) return;

    if (storageType === "S3") {
      await uploadImagesToS3(formData);
      return;
    }

    appendDriveImages(formData);
  };
  //

  const onSubmit: SubmitHandler<any> = (data: any) => {
    // Guard: never submit while an unresolved file validation error is active
    // (wrong format / size exceeded). The disabled Submit button is the primary
    // block; this is a safety net in case submit is triggered programmatically.
    if (errorMessage) {
      return;
    }

    const submitInventory = async () => {
      console.log(data);

      const updatedData = buildUpdatedData(data);

      const formData = createAssetFormData(updatedData);

      const storageType = await getStorageType();

      await handleImageUploadByStorageType(formData, storageType);

      const res = await apiService.post("api/assets/asset", formData);

      console.log("res :>> ", res);

      onClose();
      setRefresh(refresh + 1);

      addToast("Inventory Created Successfully", "success");
    };

    submitInventory().catch((err) => {
      console.log("err :>> ", err);
      const errorMessage =
        err?.response?.data ||
        err?.response?.data?.message ||
        err?.message ||
        "Something Went Wrong";
      addToast(errorMessage, "error");
    }).finally(() => {
      submittingRef.current = false;
      setIsSubmitting(false);
    });
  };

  const onInvalid: SubmitErrorHandler<any> = (errors) => {
    console.log("Form validation errors:", errors);
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchUsers(0, userSearch, false);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [userSearch]);

  return (
    <div className="bg-transparent rounded-xl">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>
          {steps[step]}
          <div className="flex justify-end mr-4	 mt-4">
            {step > 0 && (
              <button
                type="button"
                onClick={prevStep}
                className="bg-gray-500 mr-2 text-white px-4 py-2 rounded-lg hover:bg-gray-600 focus:outline-none focus:ring focus:border-gray-300"
              >
                Back
              </button>
            )}
            {step < steps.length - 1 && (
              <div className="flex justify-center gap-1">
                <button
                  type="button"
                  onClick={onClose}
                  style={{ backgroundColor: "#64748b" }}
                  className="hover:bg-slate-600 transition-all mr-2 text-white px-5 py-2 rounded-xl font-bold shadow-lg shadow-slate-200 dark:shadow-none active:scale-95 opacity-100 visible"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  style={{ backgroundColor: colors.primary }}
                  className="transition-all text-white px-5 py-2 rounded-xl font-bold shadow-lg hover:opacity-90 active:scale-95"
                >
                  Next
                </button>
              </div>
            )}
            {step === steps.length - 1 && (
              <div className="flex justify-center gap-1">
                <button
                  type="button"
                  onClick={onClose}
                  style={{ backgroundColor: "#64748b" }}
                  className="hover:bg-slate-600 transition-all mr-2 text-white px-5 py-2 rounded-xl font-bold shadow-lg shadow-slate-200 dark:shadow-none active:scale-95 opacity-100 visible"
                >
                  Cancel
                </button>
                {/*
                Disable Submit while a file validation error is active so an
                invalid file selection cannot be submitted.
              */}
                <button
                  type="submit"
                  disabled={!!errorMessage}
                  style={{
                    backgroundColor: errorMessage ? "#94a3b8" : colors.primary,
                  }}
                  className={`transition-all text-white px-5 py-2 rounded-xl font-bold shadow-lg ${errorMessage
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:opacity-90 active:scale-95"
                    }`}
                >
                  Submit
                </button>
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </div >
  );
};

export default AddProduct;
